import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Separator } from '@/components/ui/separator'
import { Info } from 'lucide-react'

/**
 * User guide component with standards documentation and URL API reference
 */
export function UserGuide() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
      {/* Main Guide Card */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            User Guide
          </CardTitle>
          <CardDescription>
            Complete documentation and standards reference for SEPA QR Code generation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full" defaultValue={["rtp"]}>
          {/* What is SEPA RTP */}
          <AccordionItem value="rtp">
            <AccordionTrigger>What is SEPA Request-to-Pay?</AccordionTrigger>
            <AccordionContent className="text-sm space-y-3">
              <p>
                <strong>SEPA Request-to-Pay (RTP)</strong> is a messaging scheme that allows a payee 
                (beneficiary) to request a payment from a payer (originator) within the Single Euro 
                Payments Area.
              </p>
              <p>
                This tool generates <strong>EPC QR codes</strong> that encode SEPA Credit Transfer 
                (SCT) payment data. When scanned with a compatible banking app, the payment details 
                are automatically pre-filled, reducing errors and simplifying the payment process.
              </p>
              <p>
                The QR code contains all necessary information for the payer to verify and authorize 
                the payment, including beneficiary name, IBAN, amount, and payment reference.
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* Standards */}
          <AccordionItem value="standards">
            <AccordionTrigger>Implemented Standards</AccordionTrigger>
            <AccordionContent className="text-sm space-y-4">
              <p>This implementation follows official European and international standards:</p>
              
              <div className="space-y-3">
                {/* EPC069-12 */}
                <div className="p-3 bg-muted/50 rounded-md">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <strong className="text-foreground">EPC069-12 v3.1</strong>
                      <span className="text-muted-foreground ml-2">(March 2024)</span>
                    </div>
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Quick Response Code Guidelines to Enable Data Capture for SCT Initiation. 
                    This is the core specification for SEPA QR codes.
                  </p>
                  <a 
                    href="https://www.europeanpaymentscouncil.eu/sites/default/files/kb/file/2024-03/EPC069-12%20v3.1%20Quick%20Response%20Code%20-%20Guidelines%20to%20Enable%20the%20Data%20Capture%20for%20the%20Initiation%20of%20an%20SCT.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline text-xs mt-2 inline-block"
                  >
                    → Download PDF from EPC
                  </a>
                </div>

                {/* ISO 7064 */}
                <div className="p-3 bg-muted/50 rounded-md">
                  <div>
                    <strong className="text-foreground">ISO 7064:2003</strong>
                    <span className="text-muted-foreground ml-2">Mod 97-10</span>
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Check character systems — Used for IBAN checksum validation. 
                    The algorithm ensures data integrity by detecting transcription errors.
                  </p>
                  <a 
                    href="https://www.iso.org/standard/31531.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline text-xs mt-2 inline-block"
                  >
                    → ISO 7064 Standard
                  </a>
                </div>

                {/* ISO 11649 */}
                <div className="p-3 bg-muted/50 rounded-md">
                  <div>
                    <strong className="text-foreground">ISO 11649:2009</strong>
                    <span className="text-muted-foreground ml-2">RF Creditor Reference</span>
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Structured creditor reference to remittance information. 
                    Enables automatic reconciliation of payments with invoices.
                  </p>
                  <a 
                    href="https://www.iso.org/standard/50649.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline text-xs mt-2 inline-block"
                  >
                    → ISO 11649 Standard
                  </a>
                </div>

                {/* ISO 20022 */}
                <div className="p-3 bg-muted/50 rounded-md">
                  <div>
                    <strong className="text-foreground">ISO 20022</strong>
                    <span className="text-muted-foreground ml-2">External Purpose Codes</span>
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Universal financial industry message scheme. Purpose codes categorize 
                    payment types (e.g., SALA for salary, GDDS for goods purchase).
                  </p>
                  <a 
                    href="https://www.iso20022.org/catalogue-messages/additional-content-messages/external-code-sets"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline text-xs mt-2 inline-block"
                  >
                    → ISO 20022 External Code Sets
                  </a>
                </div>

                {/* IBAN Registry */}
                <div className="p-3 bg-muted/50 rounded-md">
                  <div>
                    <strong className="text-foreground">SWIFT IBAN Registry</strong>
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Official registry of IBAN formats for 70+ countries. 
                    Used for country-specific IBAN validation rules.
                  </p>
                  <a 
                    href="https://www.swift.com/standards/data-standards/iban-international-bank-account-number"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline text-xs mt-2 inline-block"
                  >
                    → SWIFT IBAN Standards
                  </a>
                </div>
              </div>

              <p className="text-xs text-muted-foreground pt-2">
                This tool implements <strong>Version 2</strong> of the EPC QR payload format, 
                which makes BIC optional for EEA countries.
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* Data Elements */}
          <AccordionItem value="elements">
            <AccordionTrigger>Data Elements Reference</AccordionTrigger>
            <AccordionContent className="text-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Field</th>
                      <th className="text-left py-2 pr-4">Max</th>
                      <th className="text-left py-2 pr-4">Required</th>
                      <th className="text-left py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    <tr className="border-b">
                      <td className="py-2 pr-4">Name</td>
                      <td className="py-2 pr-4">70</td>
                      <td className="py-2 pr-4">Yes</td>
                      <td className="py-2">Beneficiary name</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">IBAN</td>
                      <td className="py-2 pr-4">34</td>
                      <td className="py-2 pr-4">Yes</td>
                      <td className="py-2">Beneficiary account</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">BIC</td>
                      <td className="py-2 pr-4">11</td>
                      <td className="py-2 pr-4">No*</td>
                      <td className="py-2">Bank identifier (*required for non-EEA)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Amount</td>
                      <td className="py-2 pr-4">12</td>
                      <td className="py-2 pr-4">No</td>
                      <td className="py-2">EUR 0.01 - 999,999,999.99</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Purpose</td>
                      <td className="py-2 pr-4">4</td>
                      <td className="py-2 pr-4">No</td>
                      <td className="py-2">ISO 20022 purpose code</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Reference</td>
                      <td className="py-2 pr-4">35</td>
                      <td className="py-2 pr-4">No**</td>
                      <td className="py-2">Structured creditor reference</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Text</td>
                      <td className="py-2 pr-4">140</td>
                      <td className="py-2 pr-4">No**</td>
                      <td className="py-2">Unstructured remittance info</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Info</td>
                      <td className="py-2 pr-4">70</td>
                      <td className="py-2 pr-4">No</td>
                      <td className="py-2">Additional beneficiary info</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground mt-3">
                ** Reference and Text are mutually exclusive
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* URL API */}
          <AccordionItem value="url-api">
            <AccordionTrigger>URL Parameter API</AccordionTrigger>
            <AccordionContent className="text-sm space-y-3">
              <p>
                Generate QR codes programmatically by passing data via URL parameters:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Parameter</th>
                      <th className="text-left py-2 pr-4">Field</th>
                      <th className="text-left py-2">Example</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    <tr className="border-b">
                      <td className="py-2 pr-4">name</td>
                      <td className="py-2 pr-4">Beneficiary Name</td>
                      <td className="py-2">name=ACME%20Corp</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">iban</td>
                      <td className="py-2 pr-4">IBAN</td>
                      <td className="py-2">iban=DE89370400440532013000</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">bic</td>
                      <td className="py-2 pr-4">BIC Code</td>
                      <td className="py-2">bic=COBADEFFXXX</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">amount</td>
                      <td className="py-2 pr-4">Amount (EUR)</td>
                      <td className="py-2">amount=125.50</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">purpose</td>
                      <td className="py-2 pr-4">Purpose Code</td>
                      <td className="py-2">purpose=GDDS</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">ref</td>
                      <td className="py-2 pr-4">Reference</td>
                      <td className="py-2">ref=RF18539007547034</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">text</td>
                      <td className="py-2 pr-4">Remittance Text</td>
                      <td className="py-2">text=Invoice%20123</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">info</td>
                      <td className="py-2 pr-4">Additional Info</td>
                      <td className="py-2">info=Thank%20you</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">instant</td>
                      <td className="py-2 pr-4">Instant SCT</td>
                      <td className="py-2">instant=true</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">mode</td>
                      <td className="py-2 pr-4">Display Mode</td>
                      <td className="py-2">mode=clean</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Separator className="my-4" />

              <p className="font-medium">Example URLs:</p>
              <div className="space-y-2">
                <div>
                  <p className="text-muted-foreground mb-1">Full form with pre-filled data:</p>
                  <code className="text-xs bg-muted p-2 rounded block overflow-x-auto">
                    ?name=ACME%20Corp&iban=DE89370400440532013000&amount=50
                  </code>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Clean QR-only mode (for embedding):</p>
                  <code className="text-xs bg-muted p-2 rounded block overflow-x-auto">
                    ?name=ACME&iban=DE89370400440532013000&mode=clean
                  </code>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Character Sets */}
          <AccordionItem value="charsets">
            <AccordionTrigger>Character Encoding</AccordionTrigger>
            <AccordionContent className="text-sm space-y-3">
              <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-md">
                <p className="font-medium text-green-800 dark:text-green-200">
                  This tool uses UTF-8 encoding by default
                </p>
                <p className="text-green-700 dark:text-green-300 text-xs mt-1">
                  UTF-8 provides the best compatibility across all banking systems and supports 
                  all European characters including accents, umlauts, and special characters.
                </p>
              </div>

              <p>
                The EPC069-12 specification supports multiple character encodings. 
                UTF-8 (code 1) is the recommended encoding and is used automatically by this tool.
              </p>

              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  View all supported encodings (for reference)
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full font-mono">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4">Code</th>
                        <th className="text-left py-2">Encoding</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b bg-muted/50"><td className="py-2 pr-4 font-bold">1</td><td className="py-2 font-bold">UTF-8 (default)</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">2</td><td className="py-2">ISO 8859-1 (Latin-1)</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">3</td><td className="py-2">ISO 8859-2 (Central European)</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">4</td><td className="py-2">ISO 8859-4 (Baltic)</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">5</td><td className="py-2">ISO 8859-5 (Cyrillic)</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">6</td><td className="py-2">ISO 8859-7 (Greek)</td></tr>
                      <tr className="border-b"><td className="py-2 pr-4">7</td><td className="py-2">ISO 8859-10 (Nordic)</td></tr>
                      <tr><td className="py-2 pr-4">8</td><td className="py-2">ISO 8859-15 (Latin-9)</td></tr>
                    </tbody>
                  </table>
                </div>
              </details>
            </AccordionContent>
          </AccordionItem>

          {/* Technical Constraints */}
          <AccordionItem value="constraints">
            <AccordionTrigger>Technical Constraints</AccordionTrigger>
            <AccordionContent className="text-sm space-y-3">
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <strong>Maximum payload:</strong> 331 bytes
                </li>
                <li>
                  <strong>QR version:</strong> Maximum version 13 (69×69 modules)
                </li>
                <li>
                  <strong>Error correction:</strong> Level M (15% recovery)
                </li>
                <li>
                  <strong>Line separator:</strong> LF (line feed) between elements
                </li>
                <li>
                  <strong>Amount format:</strong> EUR prefix, max 2 decimal places
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          {/* IBAN Validation */}
          <AccordionItem value="iban">
            <AccordionTrigger>IBAN Validation</AccordionTrigger>
            <AccordionContent className="text-sm space-y-3">
              <p>IBANs are validated using multiple checks:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <strong>Country code:</strong> Valid 2-letter ISO country code
                </li>
                <li>
                  <strong>Length:</strong> Country-specific length (e.g., DE=22, FR=27)
                </li>
                <li>
                  <strong>Format:</strong> Country-specific BBAN pattern
                </li>
                <li>
                  <strong>Checksum:</strong> ISO 7064 Mod 97-10 validation
                </li>
              </ul>
              <p className="text-muted-foreground mt-2">
                SEPA country IBANs are flagged for optimal compatibility.
              </p>
            </AccordionContent>
          </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  )
}

